<template>
  <div
    ref="categoriesListRef"
    class="categories-list"
    :class="{ 'categories-list--selected': selectedCategoryId !== null }"
  >
    <ContentTitle bolder>{{ title }}</ContentTitle>

    <div
      v-if="categories?.length"
      class="categories-list__content"
    >
      <!-- Стандартное отображение категорий для десктопа и мобильной версии без выбора -->
      <div
        v-if="!isMobile || !selectedCategoryId"
        class="categories-list__sidebar"
      >
        <CategoriesListItem
          v-for="category in categories"
          :key="category.id"
          :category="category"
          :is-active="selectedCategoryId === category.id"
          :is-categories-collapsed="!!selectedCategoryId"
          @select="selectCategory"
        />
      </div>

      <!-- Мобильный селект для выбранной категории -->
      <div
        v-if="isMobile && selectedCategoryId"
        class="categories-list__mobile-select-wrapper"
      >
        <MobileSelect
          :categories="categories"
          :selected-category-id="selectedCategoryId"
          @select="selectCategory"
          @clear="clearCategorySelection"
        />
      </div>

      <!-- Правая часть с контентом -->
      <div
        v-if="selectedCategory"
        :key="selectedCategory?.id"
        class="categories-list__products"
      >
        <!-- Слот для отображения товаров категории -->
        <slot
          :category="selectedCategory"
          name="products"
        >
          <div class="categories-list__placeholder">
            <p>Товары категории будут отображены здесь</p>
            <p>ID категории: {{ selectedCategoryId }}</p>
          </div>
        </slot>
      </div>
    </div>

    <CPreloader v-if="categories === null" />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import CPreloader from '../CPreloader/CPreloader.vue';
  import ContentTitle from '../text/ContentTitle.vue';
  import MobileSelect from './MobileSelect.vue';
  import { useClientSize } from '@/composables/useClientSize';
  import CategoriesListItem from './CategoriesListItem.vue';
  import { Category } from '@/services/category/api';

  const props = withDefaults(
    defineProps<{
      categories: Category[] | null;
      title?: string;
      initialCategoryId?: number | null;
    }>(),
    {
      title: 'Наши товары:',
      initialCategoryId: null
    }
  );

  // Состояние для отслеживания выбранной категории
  const selectedCategoryId = ref<number | null>(props.initialCategoryId);
  const { isMobile } = useClientSize();

  // Реф для контейнера списка категорий
  const categoriesListRef = ref<HTMLElement | null>(null);

  const selectedCategory = computed(() =>
    props.categories?.find(category => category.id === selectedCategoryId.value)
  );

  // Выбор категории
  const selectCategory = (categoryId: number) => {
    // Сохраняем предыдущее состояние, чтобы определить первый выбор категории
    const wasNoCategorySelected = selectedCategoryId.value === null;

    // Если категория уже выбрана, то снимаем выбор
    if (selectedCategoryId.value === categoryId) {
      selectedCategoryId.value = null;
    } else {
      selectedCategoryId.value = categoryId;
    }

    if (
      isMobile.value &&
      wasNoCategorySelected &&
      selectedCategoryId.value !== null &&
      categoriesListRef.value
    ) {
      // Скроллим к началу списка категорий только при первом выборе категории
      // Скролл с учетом отступа для фиксированного хедера (примерно 60px)
      const headerOffset = 80; // Высота хедера
      const elementPosition =
        categoriesListRef.value.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Очистить выбор категории
  const clearCategorySelection = () => {
    selectedCategoryId.value = null;
  };
</script>

<style scoped>
  .categories-list {
    padding: 30px 40px 50px;
    /* transition: all 0.5s ease-in-out; */

    &__content {
      display: flex;
      margin-top: 20px;
      position: relative;
    }

    &__sidebar {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
      width: 100%;
    }

    &__products {
      flex: 3;
      padding: 0 0 0 30px;
      opacity: 0;
      transform: translateX(30px);
      margin-left: 32px;
    }

    &__mobile-select-wrapper {
      width: 100%;
      margin-bottom: 20px;
      animation: fadeIn 0.3s ease-in-out;
    }

    &--selected {
      .categories-list__sidebar {
        display: flex;
        flex-direction: column;
        gap: 15px;
        width: 25%;
      }

      .categories-list__products {
        opacity: 1;
        transform: translateX(0);
      }
    }

    &__placeholder {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: 300px;
      background-color: rgba(var(--g-color-gray-rgb), 0.1);
      border-radius: 15px;
      border: 2px dashed var(--g-color-gray);
      font-size: 18px;
      color: var(--g-color-gray);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (--mobile) {
    .categories-list {
      padding: 12px 0;

      &__content {
        flex-direction: column;
      }

      &__sidebar {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      &__mobile-select-wrapper {
        margin-bottom: 15px;
      }

      &--selected {
        .categories-list__sidebar {
          width: 100%;
        }
      }

      &__products {
        padding: 0 0 0 0;
        margin: 0;
      }
    }
  }
</style>
